import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { formatError } from '../lib/api';
import ws from '../lib/ws';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyStarsOpen, setBuyStarsOpen] = useState(false);
  // Global header/bottom-nav badges so any page can update them
  const [counts, setCounts] = useState({ messages: 0, notifications: 0 });
  // Pages can set this to true to hide the mobile bottom nav (e.g. an active
  // DM thread). DM uses state-based routing rather than a URL :id segment,
  // so the AppShell can't infer "I'm in a focused chat" from the path alone.
  const [chatFocused, setChatFocused] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      // Only log the user out on auth failure — network blips should not nuke session
      if (e?.response?.status === 401) setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistic + authoritative wallet bump (used after Razorpay payment verify)
  const creditStars = useCallback((delta) => {
    if (!delta) return;
    setUser((u) => (u && typeof u === 'object' ? { ...u, stars: (u.stars || 0) + delta } : u));
  }, []);

  // Optimistic credit bump (after roulette spin)
  const adjustCredits = useCallback((delta) => {
    if (!delta) return;
    setUser((u) => (u && typeof u === 'object' ? { ...u, credits: Math.max(0, (u.credits || 0) + delta) } : u));
  }, []);

  // Optimistic multi-stat bump — used after activities that the server has
  // already applied (chat +xp, gift +xp, etc.) so the header chips update
  // instantly without waiting for a /me round-trip.
  const bumpStats = useCallback((deltas) => {
    if (!deltas) return;
    setUser((u) => {
      if (!u || typeof u !== 'object') return u;
      const next = { ...u };
      if (deltas.xp)      next.xp      = Math.max(0, (u.xp      || 0) + deltas.xp);
      if (deltas.stars)   next.stars   = Math.max(0, (u.stars   || 0) + deltas.stars);
      if (deltas.credits) next.credits = Math.max(0, (u.credits || 0) + deltas.credits);
      return next;
    });
  }, []);

  // Open the global BuyStars dialog (e.g. when a gift send hits "Not enough stars")
  const openBuyStars = useCallback(() => setBuyStarsOpen(true), []);

  // Fetches latest unread counts; called by polling + after read actions
  const refreshCounts = useCallback(async () => {
    try {
      const [dmsRes, notifRes] = await Promise.all([api.get('/dms'), api.get('/notifications')]);
      const unreadMsgs = (dmsRes.data || []).reduce((s, t) => s + (t.unread || 0), 0);
      const unreadNotifs = (notifRes.data || []).filter((n) => !n.read).length;
      setCounts({ messages: unreadMsgs, notifications: unreadNotifs });
    } catch { /* poll failure — keep prior counts */ }
  }, []);

  // Called from NotificationsPage onMount — instant badge clear, no race with polling
  const markNotificationsRead = useCallback(async () => {
    // Instant optimistic clear so badge updates BEFORE the network round-trip
    setCounts((c) => ({ ...c, notifications: 0 }));
    try {
      await api.post('/notifications/read-all');
    } catch {
      // If the API call fails, refetch so the badge re-syncs to the truth
      refreshCounts();
    }
  }, [refreshCounts]);

  // Best-effort: reconcile any of my "created" Razorpay orders that may have been paid
  // but never reached the verify/webhook path (browser tab died, network issue, etc.).
  const reconcilePayments = useCallback(async () => {
    try {
      const { data } = await api.post('/payments/reconcile').catch(() => ({ data: null }));
      if (data?.credited_total) creditStars(data.credited_total);
    } catch { /* silent best-effort */ }
  }, [creditStars]);

  useEffect(() => {
    // skip /me if Google callback is in flight
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  // Once the user is known, run the silent payment reconciliation + count polling
  useEffect(() => {
    if (user && typeof user === 'object' && user.user_id) {
      reconcilePayments();
      refreshCounts();
      // One-time adoption backfill — if this device has a locally-saved pet
      // from before server-side adoptions existed (or from an offline
      // adoption), push it up so the profile page shows it immediately
      // rather than waiting for the user to visit `/app/pets`. Guarded per
      // user so re-logging in on another account doesn't double-write.
      const backfillFlag = `wv_pet_backfilled_${user.user_id}`;
      if (!localStorage.getItem(backfillFlag)) {
        try {
          const localPet = JSON.parse(localStorage.getItem('wv_pet') || 'null');
          if (localPet?.id) {
            api.post('/pets/adopt', { pet_id: localPet.id })
              .then(() => localStorage.setItem(backfillFlag, '1'))
              .catch(() => {});
          } else {
            // Nothing to backfill — set the flag so we skip this check next login
            localStorage.setItem(backfillFlag, '1');
          }
        } catch {
          // malformed localStorage — ignore
        }
      }
      // Live: any inbound notification or DM event bumps the badges instantly
      const off = ws.subscribe(`user:${user.user_id}`, () => { refreshCounts(); });
      // Keep a slow safety-net poll so badges stay correct even after socket hiccups
      const t = setInterval(refreshCounts, 60000);
      return () => { off(); clearInterval(t); };
    }
  }, [user?.user_id, reconcilePayments, refreshCounts]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, displayName, dateOfBirth, opts = {}) => {
    try {
      const { data } = await api.post('/auth/register', {
        email, password, displayName, date_of_birth: dateOfBirth,
        // Optional privacy opt-in — Founder Wall public visibility.
        show_on_founder_wall: Boolean(opts.showOnFounderWall),
      });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Post-login landing = Lover's Nebula planet chat (p5) — matches email/
    // password sign-in so every traveler lands in the same welcome room.
    const redirectUrl = window.location.origin + '/app/planets/p5';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore — we're logging out anyway */ }
    setUser(false);
    window.location.href = '/';
  };

  const updateProfile = async (patch) => {
    try {
      const { data } = await api.put('/auth/profile', patch);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) };
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, register, logout, loginWithGoogle,
      updateProfile, refresh, setUser,
      // Global BuyStars dialog + helpers
      buyStarsOpen, setBuyStarsOpen, openBuyStars,
      creditStars, adjustCredits, bumpStats, reconcilePayments,
      chatFocused, setChatFocused,
      // Header/bottom-nav badge counts
      counts, refreshCounts, markNotificationsRead,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
